import React, {useEffect, useRef, useState} from "react";
import {Button, Select} from "antd";
import {ClearOutlined, CloseOutlined, PlusOutlined, SearchOutlined} from "@ant-design/icons";
import dayjs from "dayjs";
import _ from "lodash";
import {FilterItem, SearchFilterState, TableFilterProps} from "./TableFilter";
import * as styles from "./SearchFilter.module.css";
import {loadLocalStorageValue, saveLocalStorageValue} from "../../sal-ui/PersistentStorage";

const FILTER_INSTANCE_ID = "__searchFilterId";
const LEGACY_ACTIVE_FILTER_NAMES = "__searchFilterActiveNames";
const LEGACY_INACTIVE_DEFAULT_FILTER_NAMES = "__searchFilterInactiveDefaultNames";
const PERSISTENT_STATE_PREFIX = "Seal.SearchFilterState";
const DISABLE_SELECT_OPTION_TOOLTIP = "";

interface ActiveFilterItem {
    name: string;
    index?: number;
    instanceId?: string;
}

export function SearchFilter(props: TableFilterProps) {
    const [immediateMode, setImmediateMode] = useState(props.immediateMode ?? true);
    const [draftValues, setDraftValues] = useState(props.values);
    const [draftState, setDraftState] = useState<SearchFilterState>(() => loadPersistentFilterState() ?? createDefaultFilterState());
    const [editingItem, setEditingItem] = useState<ActiveFilterItem>();
    const [addFilterOpen, setAddFilterOpen] = useState(false);
    const [addFilterSearch, setAddFilterSearch] = useState("");
    const addFilterOpenRef = useRef(false);
    const addFilterSelectingRef = useRef(false);
    const addFilterSelectRef = useRef<any>(null);
    const inlineEditorRefs = useRef<Map<string, HTMLDivElement>>(new Map());
    const hasProvidedValues = draftValues !== undefined || props.values !== undefined;
    const hasDisplayValues = hasProvidedValues || props.defaultValues !== undefined;
    const resolvedValues = stripLegacyFilterState(draftValues ?? props.values ?? props.defaultValues ?? {});
    const resolvedState = props.state ?? draftState;
    const activeItemOrder = resolvedState.activeItemOrder ?? [];

    useEffect(() => {
        if (props.immediateMode !== undefined) {
            setImmediateMode(props.immediateMode);
        }
    }, [props.immediateMode]);

    useEffect(() => {
        if (immediateMode) {
            savePersistentFilterState(resolvedState);
        }
    }, [immediateMode]);

    useEffect(() => {
        if (props.values !== undefined) {
            setDraftValues(currentValues => _.isEqual(currentValues, props.values) ? currentValues : props.values);
        }
    }, [props.values]);

    useEffect(() => {
        if (props.state !== undefined) {
            setDraftState(currentState => _.isEqual(currentState, props.state) ? currentState : props.state!);
        }
    }, [props.state]);

    useEffect(() => {
        if (props.state === undefined) {
            setDraftState(loadPersistentFilterState() ?? createDefaultFilterState());
        }
    }, [props.persistentStateIdent]);

    const activeItemsInDefaultOrder: ActiveFilterItem[] = !hasDisplayValues
        ? []
        : props.items.flatMap(item => {
            if (item.multiple) {
                return (resolvedValues[item.name] || [])
                        .map((value: any, index: number) => ({name: item.name, index, instanceId: value?.[FILTER_INSTANCE_ID]}));
            }

            const activeItem = {name: item.name};

            return isActiveFilterItem(activeItem) ? [activeItem] : [];
        });
    const activeItemKeys = activeItemsInDefaultOrder.map(activeItemKey).join("|");
    const activeItems = orderActiveItems(activeItemsInDefaultOrder);

    const availableItems = props.items.filter(item => item.multiple || !activeItems.some(activeItem => activeItem.name === item.name));

    useEffect(() => {
        const activeKeys = activeItemsInDefaultOrder.map(activeItemKey);

        updateFilterState(currentState => {
            const current = currentState.activeItemOrder ?? [];
            const activeKeySet = new Set(activeKeys);
            const next = current.filter(key => activeKeySet.has(key));

            if (next.length === 0) {
                next.push(...createDefaultActiveItemOrder(activeKeys));
            }

            activeKeys.forEach(key => {
                if (!next.includes(key)) {
                    next.push(key);
                }
            });

            return _.isEqual(current, next)
                ? currentState
                : {...currentState, activeItemOrder: next};
        });
    }, [activeItemKeys]);

    useEffect(() => {
        if (immediateMode && hasProvidedValues) {
            props.onChange(resolvedValues);
        }
    }, [draftValues, immediateMode]);

    useEffect(() => {
        if (!editingItem) {
            return;
        }

        requestAnimationFrame(() => openInlineEditor(editingItem));
    }, [editingItem]);

    useEffect(() => {
        if (!addFilterOpen) {
            return;
        }

        requestAnimationFrame(() => addFilterSelectRef.current?.focus());
    }, [addFilterOpen]);

    return (
        <div className={`${styles.container} ${props.className ?? ""}`}>
            <div className={styles.searchBar}>
                <span className={styles.searchLabel}>Filter</span>

                <div className={styles.tokenList}>
                    {activeItems.map(activeItem => renderToken(activeItem))}

                    {renderAddFilter()}

                    <div className={styles.actions}>
                        {immediateMode === false && (
                            <Button className={styles.actionButton} type="primary" icon={<SearchOutlined/>} title="Search" onClick={onSearch}/>
                        )}
                        {props.defaultValues && (
                            <Button className={styles.actionButton} icon={<ClearOutlined/>} title="Reset filter" onClick={resetFilter}/>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    function renderAddFilter() {
        if (availableItems.length === 0) {
            return null;
        }

        if (!addFilterOpen) {
            return (
                <Button
                    className={styles.actionButton}
                    icon={<PlusOutlined/>}
                    title="Add filter"
                    onClick={openAddFilter}
                />
            );
        }

        return (
            <Select
                ref={addFilterSelectRef}
                className={styles.addFilterSelect}
                variant="borderless"
                size="small"
                open={addFilterOpen}
                value={null}
                searchValue={addFilterOpen ? addFilterSearch : ""}
                options={availableItems.map(createAddFilterOption)}
                placeholder="Select filter"
                popupMatchSelectWidth={260}
                showSearch
                filterOption={(input, option) => matchesFilterLabel(input, option?.label as React.ReactNode)}
                onSearch={onAddFilterSearch}
                onOpenChange={onAddFilterOpenChange}
                onSelect={addFilterItemByName}
                suffixIcon={null}
                notFoundContent="No matching filters"
            />
        );
    }

    function openAddFilter() {
        addFilterOpenRef.current = true;
        setAddFilterSearch("");
        setAddFilterOpen(true);
    }

    function renderToken(activeItem: ActiveFilterItem) {
        const item = props.items.find(filterItem => filterItem.name === activeItem.name);

        if (!item) {
            return null;
        }

        return renderInlineEditor(activeItem, item);
    }

    function renderInlineEditor(activeItem: ActiveFilterItem, item: FilterItem) {
        return (
            <div
                key={`${item.key}_${activeItem.index ?? "single"}`}
                ref={element => setInlineEditorRef(activeItem, element)}
                className={styles.inlineEditor}
            >
                <span
                    className={styles.inlineEditorLabel}
                    style={{cursor: item.labelCursor || "auto"}}
                    onClick={item.onLabelClick}
                >
                    {item.label}
                </span>
                <div className={styles.inlineEditorContent}>
                    {item.render({
                        value: getActiveValue(activeItem),
                        draftValues: resolvedValues,
                        onChange: value => item.multiple
                            ? onChangeMultipleFilterItem(activeItem, value)
                            : onChangeFilterItem(item, value),
                        onDraftValuesChange: setDraftValues
                    })}
                </div>
                <button
                    type="button"
                    className={styles.inlineEditorIcon}
                    title="Clear value"
                    onClick={() => onEditorClear(activeItem)}
                >
                    <ClearOutlined/>
                </button>
                <button
                    type="button"
                    className={styles.inlineEditorIcon}
                    title="Remove filter"
                    onClick={() => onEditorClose(activeItem)}
                >
                    <CloseOutlined/>
                </button>
            </div>
        );
    }

    function setInlineEditorRef(activeItem: ActiveFilterItem, element: HTMLDivElement | null) {
        const key = activeItemKey(activeItem);

        if (element) {
            inlineEditorRefs.current.set(key, element);
        } else {
            inlineEditorRefs.current.delete(key);
        }
    }

    function openInlineEditor(activeItem: ActiveFilterItem) {
        const editor = inlineEditorRefs.current.get(activeItemKey(activeItem));

        if (!editor) {
            return;
        }

        const focusTarget = editor.querySelector<HTMLElement>("input, textarea, [role='combobox'], [tabindex]:not([tabindex='-1'])");
        const openTarget = editor.querySelector<HTMLElement>(".ant-select-selector, .ant-picker") ?? focusTarget;

        focusTarget?.focus();

        if (!openTarget) {
            return;
        }

        openTarget.dispatchEvent(new MouseEvent("mousedown", {bubbles: true, cancelable: true, view: window}));
        openTarget.dispatchEvent(new MouseEvent("mouseup", {bubbles: true, cancelable: true, view: window}));
        openTarget.click();
    }

    function activeItemKey(activeItem: ActiveFilterItem) {
        return `${activeItem.name}:${activeItem.instanceId ?? activeItem.index ?? "single"}`;
    }

    function getActiveValue(activeItem: ActiveFilterItem) {
        const item = props.items.find(filterItem => filterItem.name === activeItem.name);

        if (!item) {
            return undefined;
        }

        if (item.multiple) {
            return resolvedValues[item.name] ? resolvedValues[item.name][activeItem.index!] : undefined;
        }

        return resolvedValues[item.name];
    }

    function onChangeFilterItem(item: FilterItem, value: any) {
        setDraftValues({
            ...resolvedValues,
            [item.name]: value
        });
        activateFilterName(item.name);
    }

    function onChangeMultipleFilterItem(activeItem: ActiveFilterItem, value: any) {
        if (activeItem.index === undefined) {
            return;
        }

        const nextValue = (resolvedValues[activeItem.name] || []).map((val: any, idx: number) => {
            if (idx === activeItem.index) {
                return preserveFilterInstanceId(val, value);
            }

            return val;
        });

        setDraftValues({
            ...resolvedValues,
            [activeItem.name]: nextValue
        });

        if (value === undefined) {
            setEditingItem(activeItem);
        }
    }

    function onEditorClear(activeItem: ActiveFilterItem) {
        const item = props.items.find(filterItem => filterItem.name === activeItem.name);

        if (!item) {
            return;
        }

        setEditingItem(undefined);

        if (item.multiple) {
            const nextValue = (resolvedValues[item.name] || []).map((val: any, idx: number) =>
                idx === activeItem.index ? clearFilterValue(val) : val
            );

            setDraftValues({
                ...resolvedValues,
                [item.name]: nextValue
            });
        } else {
            setDraftValues({
                ...resolvedValues,
                [item.name]: props.defaultValues?.[item.name]
            });
            activateFilterName(item.name);
        }
    }

    function onEditorClose(activeItem: ActiveFilterItem) {
        const item = props.items.find(filterItem => filterItem.name === activeItem.name);

        if (!item) {
            return;
        }

        setEditingItem(undefined);

        if (item.multiple) {
            setDraftValues({
                ...resolvedValues,
                [item.name]: (resolvedValues[item.name] || []).filter((val: any, idx: number) => idx !== activeItem.index)
            });
        } else {
            const clone = {...resolvedValues};

            delete clone[item.name];

            setDraftValues(clone);
            deactivateFilterName(item.name);
        }
    }

    function onAddFilterOpenChange(open: boolean) {
        addFilterOpenRef.current = open;
        setAddFilterOpen(open);
        setAddFilterSearch("");

        addFilterSelectingRef.current = false;
    }

    function onAddFilterSearch(search: string) {
        if (!addFilterOpenRef.current || addFilterSelectingRef.current) {
            return;
        }

        setAddFilterSearch(search);
    }

    function addFilterItemByName(name: string) {
        const item = availableItems.find(item => item.name === name);

        if (item) {
            addFilterSelectingRef.current = true;
            addFilterItem(item);
        }
    }

    function addFilterItem(item: FilterItem) {
        const currentValues = hasDisplayValues ? resolvedValues : {};

        if (item.multiple) {
            const currentList = currentValues[item.name] || [];
            const nextIndex = currentList.length;
            const nextActiveItem = {
                name: item.name,
                index: nextIndex,
                instanceId: createFilterInstanceId()
            };
            const newValues = {
                ...currentValues,
                [item.name]: currentList.concat(addFilterInstanceId(createInitialValue(item), nextActiveItem.instanceId))
            };

            rememberActiveItemOrder(nextActiveItem);
            setDraftValues(newValues);
            setEditingItem(nextActiveItem);
        } else {
            const nextActiveItem = {name: item.name};
            const newValues = {
                ...currentValues,
                [item.name]: createInitialValue(item)
            };

            rememberActiveItemOrder(nextActiveItem);
            setDraftValues(newValues);
            activateFilterName(item.name);
            setEditingItem(nextActiveItem);
        }

        addFilterOpenRef.current = false;
        setAddFilterOpen(false);
        setAddFilterSearch("");
    }

    function resetFilter() {
        if (props.defaultValues) {
            const values = props.defaultValues;
            const state = createDefaultFilterState(values);

            setFilterState(state, {persist: true});
            setDraftValues(values);
            props.onChange(values);
            setEditingItem(undefined);
        }
    }

    function onSearch() {
        savePersistentFilterState(resolvedState);
        props.onChange(resolvedValues);
    }

    function createInitialValue(item: FilterItem) {
        if (typeof item.initialValue === "function") {
            return item.initialValue();
        }

        if (Object.hasOwn(item, "initialValue")) {
            return item.initialValue;
        }

        return item.multiple ? {name: undefined, value: undefined} : undefined;
    }

    function orderActiveItems(items: ActiveFilterItem[]) {
        const order = new Map(activeItemOrder.map((key, index) => [key, index]));

        return [...items].sort((a, b) => {
            const aOrder = order.get(activeItemKey(a));
            const bOrder = order.get(activeItemKey(b));

            if (aOrder !== undefined && bOrder !== undefined) {
                return aOrder - bOrder;
            }

            if (aOrder !== undefined) {
                return -1;
            }

            if (bOrder !== undefined) {
                return 1;
            }

            return items.indexOf(a) - items.indexOf(b);
        });
    }

    function rememberActiveItemOrder(activeItem: ActiveFilterItem) {
        const key = activeItemKey(activeItem);

        updateFilterState(currentState => {
            const current = currentState.activeItemOrder ?? [];

            return current.includes(key)
                ? currentState
                : {...currentState, activeItemOrder: current.concat(key)};
        });
    }

    function createDefaultActiveItemOrder(activeKeys?: string[]) {
        const activeKeySet = activeKeys ? new Set(activeKeys) : undefined;

        return [
            ...(props.defaultActiveFilterNames ?? []),
            ...getActiveFilterNames()
        ]
                .filter((name, index, names) => names.indexOf(name) === index)
                .filter(name => props.items.some(item => item.name === name))
                .map(name => activeItemKey({name}))
                .filter(key => !activeKeySet || activeKeySet.has(key));
    }

    function addFilterInstanceId(value: any, instanceId: string) {
        if (value && typeof value === "object" && !Array.isArray(value) && !dayjs.isDayjs(value)) {
            return {...value, [FILTER_INSTANCE_ID]: instanceId};
        }

        return {[FILTER_INSTANCE_ID]: instanceId, value};
    }

    function clearFilterValue(value: any) {
        const instanceId = value?.[FILTER_INSTANCE_ID];

        return instanceId ? {[FILTER_INSTANCE_ID]: instanceId} : undefined;
    }

    function preserveFilterInstanceId(previousValue: any, nextValue: any) {
        const instanceId = previousValue?.[FILTER_INSTANCE_ID];

        if (!instanceId || !nextValue || typeof nextValue !== "object" || Array.isArray(nextValue) || dayjs.isDayjs(nextValue)) {
            return nextValue;
        }

        return {...nextValue, [FILTER_INSTANCE_ID]: instanceId};
    }

    function createFilterInstanceId() {
        return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    }

    function createAddFilterOption(item: FilterItem) {
        return {
            value: item.name,
            label: item.label,
            title: DISABLE_SELECT_OPTION_TOOLTIP
        };
    }

    function matchesFilterLabel(input: string, label: React.ReactNode) {
        return normalizeSearchText(filterLabelToText(label)).includes(normalizeSearchText(input));
    }

    function filterLabelToText(label: React.ReactNode): string {
        if (label === undefined || label === null || typeof label === "boolean") {
            return "";
        }

        if (typeof label === "string" || typeof label === "number") {
            return String(label);
        }

        if (Array.isArray(label)) {
            return label.map(filterLabelToText).join(" ");
        }

        if (React.isValidElement(label)) {
            return filterLabelToText(label.props.children);
        }

        return "";
    }

    function normalizeSearchText(value: string) {
        return value.toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "");
    }

    function isActiveFilterItem(activeItem: ActiveFilterItem) {
        return !isEmptyValue(getActiveValue(activeItem))
            || isEditingItem(activeItem)
            || getActiveFilterNames().includes(activeItem.name)
            || isActiveByDefault(activeItem.name);
    }

    function isEditingItem(activeItem: ActiveFilterItem) {
        return editingItem?.name === activeItem.name && editingItem?.index === activeItem.index;
    }

    function getActiveFilterNames(): string[] {
        return resolvedState.activeFilterNames ?? [];
    }

    function getInactiveDefaultFilterNames(): string[] {
        return resolvedState.inactiveDefaultFilterNames ?? [];
    }

    function activateFilterName(name: string) {
        updateFilterState(currentState => {
            const activeFilterNames = currentState.activeFilterNames ?? [];
            const inactiveDefaultFilterNames = (currentState.inactiveDefaultFilterNames ?? [])
                    .filter(inactiveName => inactiveName !== name);

            return normalizeFilterState({
                ...currentState,
                activeFilterNames: activeFilterNames.includes(name)
                        ? activeFilterNames
                        : activeFilterNames.concat(name),
                inactiveDefaultFilterNames
            });
        });
    }

    function deactivateFilterName(name: string) {
        updateFilterState(currentState => {
            const activeFilterNames = (currentState.activeFilterNames ?? [])
                    .filter(activeName => activeName !== name);
            const inactiveDefaultFilterNames = props.defaultActiveFilterNames?.includes(name)
                    ? addUnique(currentState.inactiveDefaultFilterNames ?? [], name)
                    : currentState.inactiveDefaultFilterNames;

            return normalizeFilterState({
                ...currentState,
                activeFilterNames,
                inactiveDefaultFilterNames
            });
        });
    }

    function setFilterState(state: SearchFilterState, options?: {persist?: boolean}) {
        const normalizedState = normalizeFilterState(state);

        setDraftState(normalizedState);

        if (options?.persist ?? immediateMode) {
            savePersistentFilterState(normalizedState);
        }

        props.onStateChange?.(normalizedState);
    }

    function updateFilterState(updater: (state: SearchFilterState) => SearchFilterState) {
        const nextState = normalizeFilterState(updater(resolvedState));

        if (_.isEqual(nextState, resolvedState)) {
            return;
        }

        setFilterState(nextState);
    }

    function createDefaultFilterState(values: any = props.values ?? props.defaultValues ?? {}) {
        const legacyActiveFilterNames = Array.isArray(values?.[LEGACY_ACTIVE_FILTER_NAMES])
                ? values[LEGACY_ACTIVE_FILTER_NAMES]
                : [];
        const legacyInactiveDefaultFilterNames = Array.isArray(values?.[LEGACY_INACTIVE_DEFAULT_FILTER_NAMES])
                ? values[LEGACY_INACTIVE_DEFAULT_FILTER_NAMES]
                : [];
        const legacyInactiveDefaultFilterNameSet = new Set(legacyInactiveDefaultFilterNames);
        const activeFilterNames = props.defaultActiveFilterNames
                ?.concat(legacyActiveFilterNames)
                .filter((name, index, names) => names.indexOf(name) === index)
                .filter(name => !legacyInactiveDefaultFilterNameSet.has(name))
                .filter(name => props.items.some(item => item.name === name));
        const state = normalizeFilterState({
            activeFilterNames,
            inactiveDefaultFilterNames: legacyInactiveDefaultFilterNames,
            activeItemOrder: activeFilterNames
                    ?.map(name => activeItemKey({name}))
                    .filter(key => Object.hasOwn(values, key.split(":")[0]) || props.defaultActiveFilterNames?.includes(key.split(":")[0]))
        });

        return state;
    }

    function normalizeFilterState(state: SearchFilterState): SearchFilterState {
        return {
            ...(state.activeFilterNames && state.activeFilterNames.length > 0 ? {activeFilterNames: state.activeFilterNames} : {}),
            ...(state.inactiveDefaultFilterNames && state.inactiveDefaultFilterNames.length > 0 ? {inactiveDefaultFilterNames: state.inactiveDefaultFilterNames} : {}),
            ...(state.activeItemOrder && state.activeItemOrder.length > 0 ? {activeItemOrder: state.activeItemOrder} : {})
        };
    }

    function addUnique(values: string[], value: string) {
        return values.includes(value) ? values : values.concat(value);
    }

    function loadPersistentFilterState(): SearchFilterState | undefined {
        if (!props.persistentStateIdent) {
            return undefined;
        }

        const serializedState = loadLocalStorageValue(persistentStateKey());

        if (!serializedState) {
            return undefined;
        }

        try {
            return normalizeFilterState(JSON.parse(serializedState));
        } catch {
            return undefined;
        }
    }

    function savePersistentFilterState(state: SearchFilterState) {
        if (!props.persistentStateIdent) {
            return;
        }

        saveLocalStorageValue(persistentStateKey(), JSON.stringify(state));
    }

    function persistentStateKey() {
        return `${PERSISTENT_STATE_PREFIX}.${props.persistentStateIdent}`;
    }

    function stripLegacyFilterState(values: any) {
        if (!values || typeof values !== "object" || Array.isArray(values) || dayjs.isDayjs(values)) {
            return values;
        }

        if (!Object.hasOwn(values, LEGACY_ACTIVE_FILTER_NAMES) && !Object.hasOwn(values, LEGACY_INACTIVE_DEFAULT_FILTER_NAMES)) {
            return values;
        }

        const clone = {...values};

        delete clone[LEGACY_ACTIVE_FILTER_NAMES];
        delete clone[LEGACY_INACTIVE_DEFAULT_FILTER_NAMES];

        return clone;
    }

    function isActiveByDefault(name: string) {
        return props.defaultActiveFilterNames?.includes(name)
            && !getInactiveDefaultFilterNames().includes(name);
    }

    function isEmptyValue(value: any): boolean {
        if (value === undefined || value === null) {
            return true;
        }

        if (typeof value === "string") {
            return value.trim().length === 0;
        }

        if (Array.isArray(value)) {
            return value.length === 0 || value.every(isEmptyValue);
        }

        if (dayjs.isDayjs(value)) {
            return false;
        }

        if (typeof value === "object") {
            const values = Object.entries(value)
                .filter(([key]) => key !== FILTER_INSTANCE_ID)
                .map(([, val]) => val);

            return values.length === 0 || values.every(isEmptyValue);
        }

        return false;
    }
}
